import { evaluateAuthority, type AuthorityRule } from "@/lovable-demo/lib/governance/authority";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import type { ProblemRecord } from "./types";

export type CarryFactorResult = { ok: true } | { ok: false; error: string };
export type CarryFactorRpc = (
  fn: "financial_set_record_carry_factor" | "financial_set_default_carry_factor",
  args: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

export const CARRY_FACTOR_TOOLTIP =
  "The monthly cost of leaving remaining exposure unaddressed: continued claims accrual, containment and inspection cost, and carrying cost.";

export function validateCarryFactor(value: number, rationale: string): string | null {
  if (!Number.isFinite(value) || value <= 0 || value > 1)
    return "Enter a monthly carry factor greater than 0% and no more than 100%.";
  if (rationale.trim().length < 10) return "A rationale of at least 10 characters is required.";
  return null;
}

function authorised(
  record: Pick<ProblemRecord, "riskScore" | "financial">,
  roles: AppRole[],
  rules: AuthorityRule[],
  decisionType: string,
) {
  return evaluateAuthority({
    decisionType,
    record,
    roles,
    rules,
    exactRoles: ["quality_director", "admin"],
  });
}

export async function submitRecordCarryFactor(
  rpc: CarryFactorRpc,
  args: {
    record: ProblemRecord;
    newValue: number;
    rationale: string;
    roles: AppRole[];
    rules: AuthorityRule[];
  },
): Promise<CarryFactorResult> {
  const invalid = validateCarryFactor(args.newValue, args.rationale);
  if (invalid) return { ok: false, error: invalid };
  if (args.newValue === args.record.financial.monthlyCarryFactor)
    return { ok: false, error: "The new carry factor matches the current value." };
  const verdict = authorised(
    args.record,
    args.roles,
    args.rules,
    "financial.carry_factor_override",
  );
  if (!verdict.authorised)
    return { ok: false, error: "Quality Director authority is required. Nothing was recorded." };
  const { error } = await rpc("financial_set_record_carry_factor", {
    p_record_id: args.record.id,
    p_new_value: args.newValue,
    p_rationale: args.rationale.trim(),
  });
  return error ? { ok: false, error: `Nothing was recorded. ${error.message}` } : { ok: true };
}

export async function submitDefaultCarryFactor(
  rpc: CarryFactorRpc,
  args: {
    currentValue: number;
    newValue: number;
    rationale: string;
    roles: AppRole[];
    rules: AuthorityRule[];
  },
): Promise<CarryFactorResult> {
  const invalid = validateCarryFactor(args.newValue, args.rationale);
  if (invalid) return { ok: false, error: invalid };
  if (args.newValue === args.currentValue)
    return { ok: false, error: "The new carry factor matches the current value." };
  const verdict = authorised(
    { riskScore: 0, financial: { exposure: 0 } as ProblemRecord["financial"] },
    args.roles,
    args.rules,
    "financial.carry_factor_default",
  );
  if (!verdict.authorised)
    return { ok: false, error: "Quality Director authority is required. Nothing was recorded." };
  const { error } = await rpc("financial_set_default_carry_factor", {
    p_new_value: args.newValue,
    p_rationale: args.rationale.trim(),
  });
  return error ? { ok: false, error: `Nothing was recorded. ${error.message}` } : { ok: true };
}
